import { Badge, type BadgeColor } from '@clay-run/glaze/badge';
import { Text } from '@clay-run/glaze/text';
import { paceBucket, type PaceBucket } from '@shared/flow/account-pace';
import type { DelphiAccountDeal } from '../../../../../../mini-apps/_shared/delphi-account-deals';
import { formatPace, healthLabel, paceLabel } from './format';

/**
 * The Growth Strategist ledger's two pills.
 *
 * Glaze `Badge size="xs"` is already the pill with the light fill and
 * border, so the one override is width: a minimum per pill class, passed as a
 * class name because Badge has no width prop, so every pill in a column is
 * identical. Both minimums come from the GS design (egs-view at e7603325b), 76px and
 * 52px, written on Tailwind's spacing scale (`min-w-19`, `min-w-13`).
 * `min-w-` rather than `w-` so a longer label widens the pill instead of
 * clipping inside it.
 *
 * Colours are the Badge palette: red, orange, green, and `secondary` for an
 * honest absence.
 */

const HEALTH_WIDTH_CLASS = 'min-w-19 justify-center';
const PACE_WIDTH_CLASS = 'min-w-13 justify-center tabular-nums';

const HEALTH_COLORS = new Map<string, BadgeColor>([
  ['Red', 'red'],
  ['Yellow', 'orange'],
  ['Green', 'green'],
]);

/** A rating outside the three Salesforce values, or none, or a blank one, reads in the neutral colour. */
function healthColor(health: string | null): BadgeColor {
  return (health?.trim() ? HEALTH_COLORS.get(health) : undefined) ?? 'secondary';
}

/** On pace and over pace are both green (healthy), under is orange, severe is red. */
function paceColor(bucket: PaceBucket): BadgeColor {
  switch (bucket) {
    case 'severe':
      return 'red';
    case 'under':
      return 'orange';
    case 'on':
    case 'over':
      return 'green';
    case 'unknown':
      return 'secondary';
  }
}

/** "Green", "Yellow", "Red" or "No Rating" at a fixed minimum width. */
export function HealthPill({ health }: { health: string | null }) {
  return (
    <Badge size="xs" color={healthColor(health)} className={HEALTH_WIDTH_CLASS}>
      {healthLabel(health)}
    </Badge>
  );
}

const CALL_COLORS: Record<DelphiAccountDeal['ci']['color'], BadgeColor> = {
  green: 'green',
  orange: 'orange',
  red: 'red',
  gray: 'secondary',
  blue: 'blue',
};

/** Delphi's call pill for a deal, its label as sent, at the health pill's minimum width. */
export function DelphiCallPill({ ci }: { ci: DelphiAccountDeal['ci'] }) {
  return (
    <Badge size="xs" color={CALL_COLORS[ci.color]} className={HEALTH_WIDTH_CLASS}>
      {ci.label}
    </Badge>
  );
}

/**
 * The ratio pill ("122%", or the empty glyph with no ratio) with the bucket
 * label as plain secondary text ("Over Pace", "No Pacing"): under the pill in
 * the ledger cell (`stacked`, the default), beside it on a card's facts row
 * (`horizontal`). The bucket derives from the ratio, so pill and label cannot
 * disagree. The stack is centred on itself, so in the Health layout's centred
 * column the pill sits over the middle of its label, as every other cell sits
 * over the middle of the column.
 */
export function PacePill({
  ratio,
  layout = 'stacked',
}: {
  ratio: number | null | undefined;
  layout?: 'stacked' | 'horizontal';
}) {
  const bucket = paceBucket(ratio);
  return (
    <span className={layout === 'horizontal' ? 'inline-flex items-center gap-2 whitespace-nowrap' : 'inline-flex flex-col items-center gap-0.5'}>
      <Badge size="xs" color={paceColor(bucket)} className={PACE_WIDTH_CLASS}>
        {ratio != null && Number.isFinite(ratio) ? formatPace(ratio) : '—'}
      </Badge>
      <Text size="xs" variant="secondary" render={<span />}>
        {paceLabel(bucket)}
      </Text>
    </span>
  );
}
